// src/components/chat/ChatMessages.tsx

import * as React from "react";
import { ChatMessagesProps } from "./types/ChatUITypes";
import MessageBubble from "./MessageBubble";
import { CitationTooltipProvider } from "../../contexts/CitationTooltipContext";
import { DocumentCitationTooltipProvider } from "../../contexts/DocumentCitationTooltipContext";
import { PlaybookCitationTooltipProvider } from "../../contexts/PlaybookCitationTooltipContext";
import { CatylexCitationTooltipProvider } from "../../contexts/CatylexCitationTooltipContext";
import { GlobalCitationTooltip } from "./GlobalCitationTooltip";
import { GlobalDocumentCitationTooltip } from "./GlobalDocumentCitationTooltip";
import { GlobalPlaybookCitationTooltip } from "./GlobalPlaybookCitationTooltip";
import { GlobalCatylexCitationTooltip } from "./GlobalCatylexCitationTooltip";
import {
  hasTakenQuickAnalysisRoute,
  isDraftCertificateAnswer,
} from "./draftCertificate/certificateRoute";
import { createLogger } from "../../utils/logger";

// Create a logger specifically for the ChatMessages component
const log = createLogger("ChatMessages");

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  isStreaming,
  messageSelectedText = new Map(), // Default to empty map
  onPromptClick, // Prompt Coach click handler
  onFactCheck, // Fact-check handler
  onPlaybookSelected, // Playbook selection handler
  onDraftCertificate,
  scrollContainerRef,
}) => {
  // The Draft Certificate is offered on the last completed answer, and only in
  // a conversation the Quick Analysis action started: it is the closing step
  // of that workflow, not a general feature. The certificate itself does not
  // carry the offer - re-running it unchanged answers nothing - but the next
  // answer does, so a certificate can be regenerated once the document has
  // been worked on.
  const offerDraftCertificate =
    !!onDraftCertificate && !isStreaming && hasTakenQuickAnalysisRoute(messages);
  const lastMessageIndex = messages.length - 1;
  // Log rendering information
  React.useEffect(() => {
    log.debug("ChatMessages rendering", {
      messageCount: messages.length,
      isStreaming,
      selectedTextCount: messageSelectedText.size,
    });
  }, [messages.length, isStreaming, messageSelectedText.size]);

  /**
   * Determine if a specific message is currently being streamed
   *
   * A message is considered to be streaming if:
   * 1. Overall streaming is active (isStreaming = true), AND
   * 2. This is the last message in the conversation, AND
   * 3. The message is from the assistant
   *
   * This logic ensures that only the actively streaming message shows streaming indicators
   * while preserving the behavior for completed messages.
   */
  const isMessageStreaming = React.useCallback(
    (messageIndex: number): boolean => {
      const message = messages[messageIndex];
      const isLastMessage = messageIndex === messages.length - 1;
      const isFromAssistant = message.role === "assistant";

      const streamingState = isStreaming && isLastMessage && isFromAssistant;

      log.trace("Message streaming state calculation", {
        messageIndex,
        messageId: message.id,
        isStreaming,
        isLastMessage,
        isFromAssistant,
        result: streamingState,
      });

      return streamingState;
    },
    [messages, isStreaming]
  );

  // Clean container styles that don't interfere with child components
  const messagesContainerStyle: React.CSSProperties = {
    // Minimal styling that focuses on layout, not content formatting
    display: "flex",
    flexDirection: "column",
    gap: "0px", // Let MessageBubble handle its own spacing
    width: "100%",
    height: "auto",
    // No padding, font sizing, or text formatting that could interfere with markdown
  };

  const streamingIndicatorStyle: React.CSSProperties = {
    display: "inline-block",
    width: "12px",
    height: "12px",
    backgroundColor: "black",
    marginLeft: "8px",
    marginTop: "8px",
    animation: "pulsate 1.5s ease-in-out infinite",
    borderRadius: "50%",
  };

  return (
    <CitationTooltipProvider scrollContainerRef={scrollContainerRef}>
      <DocumentCitationTooltipProvider scrollContainerRef={scrollContainerRef}>
        <PlaybookCitationTooltipProvider scrollContainerRef={scrollContainerRef}>
          <CatylexCitationTooltipProvider scrollContainerRef={scrollContainerRef}>
            <div style={messagesContainerStyle}>
              {messages.map((message, index) => {
                // Determine streaming state for this specific message
                const isThisMessageStreaming = isMessageStreaming(index);

                // Get selected text for this message (if any)
                const selectedTextForMessage = messageSelectedText.get(message.id) || "";

                // Only log web search state changes for assistant messages
                if (message.role === "assistant" && message.webSearchEvents?.length) {
                  log.debug("🎯 Assistant message has web search events", {
                    messageId: message.id,
                    webSearchEventCount: message.webSearchEvents.length,
                    isSearching: message.isSearching,
                  });
                }

                return (
                  <MessageBubble
                    key={message.id || index}
                    message={message}
                    isStreaming={isThisMessageStreaming}
                    selectedText={selectedTextForMessage} // Pass selected text (plain text)
                    // Pass web search state from the message itself
                    webSearchEvents={message.webSearchEvents}
                    isSearching={message.isSearching || false}
                    // Pass web fetch state from the message itself
                    webFetchEvents={message.webFetchEvents}
                    isFetching={message.isFetching || false}
                    // Pass auto-redline outputs from the message itself
                    autoRedlineOutputs={message.autoRedlineOutputs}
                    // Pass composer mode state from message creation time
                    composerModeActiveOnCreation={message.composerModeActiveOnCreation}
                    // Pass workflow form payload from the message
                    formRequestPayload={message.formRequestPayload}
                    // Pass playbook result from the message
                    playbookResult={message.playbookResult}
                    // Pass Prompt Coach click handler
                    onPromptClick={onPromptClick}
                    // Pass fact-check handler
                    onFactCheck={onFactCheck}
                    // Pass playbook selection handler
                    onPlaybookSelected={onPlaybookSelected}
                    onDraftCertificate={
                      offerDraftCertificate &&
                      index === lastMessageIndex &&
                      message.role === "assistant" &&
                      !isDraftCertificateAnswer(messages, index)
                        ? onDraftCertificate
                        : undefined
                    }
                    isDraftCertificateAnswer={isDraftCertificateAnswer(messages, index)}
                    // Pass tool processing state from the message itself
                    isProcessingTool={message.isProcessingTool || false}
                  />
                );
              })}

              {/* Global streaming indicator (appears when streaming but no specific message to attach to) */}
              {isStreaming && <div style={streamingIndicatorStyle} />}
            </div>

            {/* Global citation tooltips - rendered once at root level to prevent flickering during streaming */}
            <GlobalCitationTooltip />
            <GlobalDocumentCitationTooltip />
            <GlobalPlaybookCitationTooltip />
            <GlobalCatylexCitationTooltip />
          </CatylexCitationTooltipProvider>
        </PlaybookCitationTooltipProvider>
      </DocumentCitationTooltipProvider>
    </CitationTooltipProvider>
  );
};

export default ChatMessages;
